import React, { useState } from 'react';
import { HStack, Spinner, Text } from '@chakra-ui/react';
import { Stack } from '@chakra-ui/layout';
import { useHistory } from 'react-router';
import { Button } from '~/components/Button';
import { Footer } from '~/components/Footer';

interface StepperProps {
  color?: string;
  width?: string;
  steps: Steps[];
  bgColor?: string;
  isLoading?: boolean;
  completed: () => void;
  completedText?: string;
  selecionado: (step: Steps) => void;
  thirdButtonText?: string;
  thirdButtonAction?: () => void;
}

export interface Steps {
  id: number;
  name: string;
  selected?: boolean;
  disabled?: boolean;
}

export const Stepper: React.FC<StepperProps> = ({
  steps,
  color = '#6b27a0',
  width = '60px',
  bgColor = '#b98fdb',
  isLoading = false,
  completed,
  completedText = 'Finalizar',
  selecionado,
  children,
  thirdButtonText,
  thirdButtonAction,
}) => {
  const [stepsControl, setSteps] = useState(steps);
  const history = useHistory();
  const disabledColors = {
    color: '#717171',
    bgColor: '#e3e3e6',
    nameColor: '#2c2a29',
  };
  const selectedColors = {
    color,
    bgColor,
    nameColor: '#6b27a0',
  };
  const getIndexUltimoSelecionado = () =>
    stepsControl.reduceRight(
      (lastIndex, step, index) =>
        step.selected && lastIndex === -1 ? index : lastIndex,
      -1,
    );

  const getColor = (step: Steps) => {
    if (step.selected) {
      const indexUltimoSelecionado = getIndexUltimoSelecionado();
      return {
        ...selectedColors,
        nameColor:
          stepsControl[indexUltimoSelecionado]?.id === step?.id
            ? selectedColors.nameColor
            : disabledColors.nameColor,
      };
    }
    return { ...disabledColors, nameColor: disabledColors.color };
  };

  const isUltimoSelecionado = () => {
    const idxUltimoSelecionado = getIndexUltimoSelecionado();
    return (
      stepsControl[stepsControl.length - 1]?.id ===
      stepsControl[idxUltimoSelecionado]?.id
    );
  };

  const isPrimeiroSelecionado = () => {
    const indexUltimoSelecionado = getIndexUltimoSelecionado();
    return stepsControl[0]?.id === stepsControl[indexUltimoSelecionado]?.id;
  };

  const todosSelecionados = () => stepsControl.every(step => step.selected);

  const voltar = () => {
    if (isPrimeiroSelecionado()) {
      history.goBack();
      return;
    }

    const indexUltimoSelecionado = getIndexUltimoSelecionado();

    const stepsAtualizados = stepsControl.map((step, index) =>
      index === indexUltimoSelecionado ? { ...step, selected: false } : step,
    );

    setSteps(stepsAtualizados);
    selecionado(stepsAtualizados[indexUltimoSelecionado - 1]);
  };

  const proximo = () => {
    if (todosSelecionados()) {
      completed();
      return;
    }

    const indexUltimoSelecionado = getIndexUltimoSelecionado();

    const stepsAtualizados = stepsControl.map((step, index) =>
      index === indexUltimoSelecionado + 1 ? { ...step, selected: true } : step,
    );

    setSteps(stepsAtualizados);
    selecionado(stepsAtualizados[indexUltimoSelecionado + 1]);
  };

  return (
    <Stack w="100%" alignItems="center">
      <HStack justifyContent="space-between" p={5} w="40%">
        {stepsControl.map((step, index) => (
          <Stack m={0} marginInlineStart={0} alignItems="center" key={step.id}>
            <HStack
              key={step.id}
              w={width}
              height={width}
              bgColor={getColor(step).bgColor}
              color={getColor(step).color}
              p={2}
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontWeight="bold" fontSize="18px">
                {step.id}
              </Text>
            </HStack>
            <Text
              textAlign="center"
              fontSize="14px"
              color={getColor(step).nameColor}
              fontWeight="bold"
            >
              {step.name}
            </Text>
          </Stack>
        ))}
      </HStack>
      <Stack minH="calc(100vh - 328px)" w="100%">
        {children}
      </Stack>
      <Footer justify={thirdButtonText ? 'space-between' : 'flex-end'}>
        {thirdButtonText && (
          <Button
            variant="secondary"
            onClick={thirdButtonAction}
            isDisabled={isLoading}
          >
            {thirdButtonText}
          </Button>
        )}
        <HStack gridGap={3}>
          <Button
            variant="secondary"
            onClick={() => voltar()}
            isDisabled={isLoading}
          >
            Voltar
          </Button>
          <Button
            type="button"
            isDisabled={
              isLoading || steps[getIndexUltimoSelecionado()].disabled
            }
            onClick={() => proximo()}
          >
            {isUltimoSelecionado() ? completedText : 'Próxima etapa'}
            {isLoading && <Spinner style={{ marginLeft: '10px' }} />}
          </Button>
        </HStack>
      </Footer>
    </Stack>
  );
};
